import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  IconButton,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  CircularProgress,
  Grid,
  Stack,
  Snackbar,
  Menu,
  ListItemButton
} from '@mui/material';
import {
  Add as AddIcon,
  Event as EventIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  CheckBox,
  CheckBoxOutlineBlank,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  Category as CategoryIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  Slideshow as PresentationIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function OrganizationalEvents() {
  const [events, setEvents] = useState([]);
  const [openEventDialog, setOpenEventDialog] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [customEventType, setCustomEventType] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState('');
  const [employeeId, setEmployeeId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectAllEmployees, setSelectAllEmployees] = useState(false);
  const [addingEvent, setAddingEvent] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [editingEvent, setEditingEvent] = useState(null);
  const [updatingEvent, setUpdatingEvent] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEventForMenu, setSelectedEventForMenu] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);
  
  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [newEvent, setNewEvent] = useState({ 
    title: '', 
    date: '', 
    time: '',
    location: '',
    type: 'Meeting',
    customType: '',
    assignedEmployees: [],
    attendees: '',
    status: 'pending'
  });

  const eventTypes = [
    'Meeting',
    'Workshop',
    'Training',
    'Conference',
    'Seminar',
    'Presentation',
    'Review',
    'Planning Session',
    'Custom'
  ];

  // Event type icons mapping
  const getEventTypeIcon = (type) => {
    const iconMap = {
      'Meeting': <GroupIcon />,
      'Workshop': <SettingsIcon />,
      'Training': <SchoolIcon />,
      'Conference': <BusinessIcon />,
      'Seminar': <PresentationIcon />,
      'Presentation': <PresentationIcon />,
      'Review': <AssessmentIcon />,
      'Planning Session': <ScheduleIcon />,
      'Custom': <EventIcon />
    };
    return iconMap[type] || <EventIcon />;
  };

  // Snackbar handler
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Menu handlers
  const handleMenuClick = (event, eventData) => {
    setAnchorEl(event.currentTarget);
    setSelectedEventForMenu(eventData);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEventForMenu(null);
  };

  // Get user role from localStorage or sessionStorage
  const getUserRole = () => {
    const role = localStorage?.getItem("role") || sessionStorage?.getItem("role") || "employee";
    setUserRole(role.toLowerCase());
    return role.toLowerCase();
  };

  // Fetch current user to get user info
  const fetchCurrentUser = async () => {
    try {
      
      let response;
           const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      try {
       
        response = await fetch(`${API_URL}/api/user/current?company_id=${company_id}`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          }
        });
      } catch (fetchError) {
        response = await fetch(`${API_URL}/api/user/profile`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();

      if (!userData) {
        throw new Error('User data not found');
      }

      const role = userData.role || userData.userRole || userData.position || getUserRole();
      const empId = userData.employee_id || userData.employeeId || userData.id || userData.userId || null;

      setUserRole(role.toLowerCase());
      setEmployeeId(empId);
      setCurrentUser(userData);
      
      
      return {
        role: role.toLowerCase(),
        employeeId: empId,
        userData
      };
    } catch (err) {
      //console.error("Error fetching current user:", err);
      const fallbackRole = getUserRole();
      setError('Failed to fetch user information. Using cached role.');
      return {
        role: fallbackRole,
        employeeId: null,
        userData: null
      };
    }
  };

  // Fetch employees from API (only for managers/HR)
  const fetchEmployees = async () => {
    try {
           const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await fetch(`${API_URL}/api/employees`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const employeesData = await response.json();
      
      // Handle different response structures
      let processedEmployees = [];
      if (Array.isArray(employeesData)) {
        processedEmployees = employeesData;
      } else if (employeesData.data && Array.isArray(employeesData.data)) {
        processedEmployees = employeesData.data;
      } else if (employeesData.employees && Array.isArray(employeesData.employees)) {
        processedEmployees = employeesData.employees;
      }
      
      // Ensure each employee has required fields
      processedEmployees = processedEmployees.map(emp => ({
        id: emp.id || emp.employee_id,
        name: emp.name || `${emp.firstName || emp.first_name || ''} ${emp.lastName || emp.last_name || ''}`.trim(),
        firstName: emp.firstName || emp.first_name || '',
        lastName: emp.lastName || emp.last_name || '',
        department: emp.department || '',
        position: emp.position || emp.role || '',
        email: emp.email || ''
      }));
      
      setEmployees(processedEmployees);
    } catch (error) {
      //console.error('Error fetching employees:', error);
      setError('Failed to fetch employees list');
      setEmployees([]);
    }
  };

  // Fetch events from API - FILTER OUT COMPLETED EVENTS COMPLETELY
  const fetchEvents = async (userInfo) => {
    try {
      const token = localStorage.getItem('token');
  
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      let endpoint;
  
      if (['project-manager','product-owner','cpo', 'hr', 'admin'].includes(userInfo.role)) {
        endpoint = `${API_URL}/api/allevents`;
      } else {
        endpoint = `${API_URL}/api/em/${userInfo.employeeId}`;
      }
  
  
      const response = await fetch(endpoint, {
        method: "GET",
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const eventsData = await response.json();
  
  
      let events = [];
  
      if (eventsData && eventsData.success && Array.isArray(eventsData.data)) {
        events = eventsData.data;
      } else if (Array.isArray(eventsData)) {
        events = eventsData;
      } else if (eventsData && Array.isArray(eventsData.events)) {
        events = eventsData.events;
      }
  
      const filteredEvents = events.filter(event => {
        const status = event.status?.toLowerCase();
  
        return status !== 'completed' &&
               status !== 'complete' &&
               status !== 'cancelled' &&
               status !== 'cancle';
      });
  
      const processedEvents = filteredEvents.map(event => ({
        ...event,
        assignedCount:
          event.assignedCount ||
          event.assigned_count ||
          (event.assignedEmployees === 'all' ||
          event.assigned_employees === 'all'
            ? 'All'
            : Array.isArray(event.assignedEmployees)
            ? event.assignedEmployees.length
            : Array.isArray(event.assigned_employees)
            ? event.assigned_employees.length
            : 0)
      }));
  
      setEvents(processedEvents || []);
    } catch (error) {
      //console.error('Error fetching events:', error);
      setError('Failed to fetch events. Please try refreshing.');
      setEvents([]);
    }
  };

  // Initialize component
  useEffect(() => {
    const initializeComponent = async () => {
      setLoading(true);
      setError('');
      
      try {
        const userInfo = await fetchCurrentUser();
        
        if (userInfo && (userInfo.employeeId || userInfo.role)) {
          await fetchEvents(userInfo);
          
          if (userInfo.role === 'project-manager' || userInfo.role === 'product-owner' || userInfo.role === 'cpo' || userInfo.role === 'hr' || userInfo.role === 'admin') {
            await fetchEmployees();
          }
          
          const token = localStorage.getItem('token');
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.company_id) {
              const res = await fetch(`${API_URL}/api/cmp/${payload.company_id}`, { headers: { Authorization: `Bearer ${token}` } });
              const data = await res.json();
              if (data && data.data) setCompanyConfig(data.data);
            }
          }
        } else {
          setError('Unable to load user information');
        }
      } catch (err) {
        //console.error('Error initializing component:', err);
        setError('Failed to initialize. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    initializeComponent();
  }, []);

  // Refresh data function
  const refreshData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const userInfo = await fetchCurrentUser();
      if (userInfo && (userInfo.employeeId || userInfo.role)) {
        await fetchEvents(userInfo);
        if (userInfo.role === 'manager' || userInfo.role === 'hr' || userInfo.role === 'admin') {
          await fetchEmployees();
        }
      }
      showSnackbar('Data refreshed successfully');
    } catch (err) {
      //console.error('Error refreshing data:', err);
      setError('Failed to refresh data');
      showSnackbar('Failed to refresh data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getEventTypeColor = (type) => {
    const colorMap = {
      'Meeting': 'primary',
      'Workshop': 'success',
      'Training': 'warning',
      'Conference': 'error',
      'Seminar': 'info',
      'Presentation': 'secondary',
      'Review': 'primary',
      'Planning Session': 'success',
      'Custom': 'default'
    };
    return colorMap[type] || 'default';
  };

  // Get status color for chips
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!newEvent.title.trim()) {
      errors.title = 'Event title is required';
    }
    
    if (!newEvent.date) {
      errors.date = 'Event date is required';
    }
    
    if (!newEvent.time.trim()) {
      errors.time = 'Event time is required';
    }
    
    if (newEvent.type === 'Custom' && !newEvent.customType.trim()) {
      errors.customType = 'Custom event type is required';
    }
    
    if (!selectAllEmployees && newEvent.assignedEmployees.length === 0) {
      errors.assignedEmployees = 'Please assign at least one employee or select all employees';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle select all employees
  const handleSelectAllEmployees = (checked) => {
    setSelectAllEmployees(checked);
    if (checked) {
      setNewEvent({...newEvent, assignedEmployees: []});
    }
    // Clear validation errors
    if (validationErrors.assignedEmployees) {
      const newErrors = { ...validationErrors };
      delete newErrors.assignedEmployees;
      setValidationErrors(newErrors);
    }
  };

  // Handle individual employee selection
  const handleEmployeeSelection = (employee, checked) => {
    let updatedEmployees;
    
    if (checked) {
      // Add employee if not already selected
      const isAlreadySelected = newEvent.assignedEmployees.some(emp => emp.id === employee.id);
      if (!isAlreadySelected) {
        updatedEmployees = [...newEvent.assignedEmployees, employee];
      } else {
        updatedEmployees = newEvent.assignedEmployees;
      }
    } else {
      // Remove employee
      updatedEmployees = newEvent.assignedEmployees.filter(emp => emp.id !== employee.id);
    }
    
    setNewEvent({
      ...newEvent, 
      assignedEmployees: updatedEmployees
    });
    
    // Clear validation errors if employees are selected
    if (updatedEmployees.length > 0 && validationErrors.assignedEmployees) {
      const newErrors = { ...validationErrors };
      delete newErrors.assignedEmployees;
      setValidationErrors(newErrors);
    }
  };

  // Quick status update function
  const updateEventStatus = async (eventId, status) => {
    setUpdatingEvent(true);
    try {
           const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await fetch(`${API_URL}/api/events/${eventId}/status`, {
        method: 'PATCH',
        credentials: 'include'  ,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      showSnackbar(`Event marked as ${status}!`);
      
      // Refresh events to update UI immediately
      const userInfo = { role: userRole, employeeId };
      await fetchEvents(userInfo);
      
    } catch (error) {
      //console.error('Error updating event status:', error);
      showSnackbar('Failed to update event status', 'error');
    } finally {
      setUpdatingEvent(false);
      handleMenuClose();
    }
  };

  // Mark event as completed - this will hide the event from UI
  const markEventAsCompleted = async (eventId) => {
    await updateEventStatus(eventId, 'completed');
  };

  // Start editing event
  const startEditEvent = (event) => {
    setEditingEvent(event);
    setNewEvent({
      title: event.title || '',
      date: event.date || '',
      time: event.time || '',
      location: event.location || '',
      type: event.type || 'Meeting',
      customType: event.type && !eventTypes.includes(event.type) ? event.type : '',
      assignedEmployees: [], // Will be populated if needed
      attendees: event.attendees || '',
      status: event.status || 'pending'
    });
    setCustomEventType(event.type && !eventTypes.includes(event.type));
    setOpenEventDialog(true);
    handleMenuClose();
  };

  // Update existing event
  const updateEvent = async () => {
    if (!validateForm()) {
      setError('Please correct the errors below');
      return;
    }

    setAddingEvent(true);
    setError('');

    try {
      const finalEventType = newEvent.type === 'Custom' ? newEvent.customType : newEvent.type;
      
      let assignedEmployeesData;
      if (selectAllEmployees) {
        assignedEmployeesData = 'all';
      } else {
        assignedEmployeesData = newEvent.assignedEmployees.map(emp => emp.id);
      }
      
      const eventData = {
        title: newEvent.title.trim(),
        date: newEvent.date,
        time: newEvent.time.trim(),
        location: newEvent.location.trim(),
        type: finalEventType,
        attendees: parseInt(newEvent.attendees) || 0,
        assignedEmployees: assignedEmployeesData,
        status: newEvent.status
      };

     const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await fetch(`${API_URL}/api/events/${editingEvent.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      showSnackbar('Event updated successfully!');
      
      const userInfo = { role: userRole, employeeId };
      await fetchEvents(userInfo);
      resetForm();
      
    } catch (error) {
      //console.error('Error updating event:', error);
      setError(error.message || 'Failed to update event');
      showSnackbar('Failed to update event', 'error');
    } finally {
      setAddingEvent(false);
    }
  };

  // Add new event with proper validation
  const addNewEvent = async () => {
    if (!validateForm()) {
      setError('Please correct the errors below');
      return;
    }

    setAddingEvent(true);
    setError('');

    try {
      const finalEventType = newEvent.type === 'Custom' ? newEvent.customType : newEvent.type;
      
      let assignedEmployeesData;
      if (selectAllEmployees) {
        assignedEmployeesData = 'all';
      } else {
        assignedEmployeesData = newEvent.assignedEmployees.map(emp => emp.id);
      }
      
      const eventData = {
        title: newEvent.title.trim(),
        date: newEvent.date,
        time: newEvent.time.trim(),
        location: newEvent.location.trim(),
        type: finalEventType,
        attendees: parseInt(newEvent.attendees) || 0,
        assignedEmployees: assignedEmployeesData,
        createdBy: employeeId ? employeeId.toString() : "1",
        status: 'pending'
      };

     const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await fetch(`${API_URL}/api/events`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      showSnackbar('Event created successfully! Email notifications have been sent.');
      
      const userInfo = { role: userRole, employeeId };
      await fetchEvents(userInfo);
      resetForm();
      
    } catch (error) {
      //console.error('Error creating event:', error);
      setError(error.message || 'Failed to create event');
      showSnackbar('Failed to create event', 'error');
    } finally {
      setAddingEvent(false);
    }
  };

  const resetForm = () => {
    setNewEvent({ 
      title: '', 
      date: '', 
      time: '',
      location: '',
      type: 'Meeting',
      customType: '',
      assignedEmployees: [],
      attendees: '',
      status: 'pending'
    });
    setCustomEventType(false);
    setSelectAllEmployees(false);
    setOpenEventDialog(false);
    setEditingEvent(null);
    setError('');
    setValidationErrors({});
  };

  const formatDate = (dateString) => {
    try {
      const options = {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      };
      if (companyConfig?.timezone) {
        options.timeZone = companyConfig.timezone;
      }
      return new Date(dateString).toLocaleDateString('en-US', options);
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch (error) {
      return timeString;
    }
  };

  const canCreateEvents = userRole === 'manager' || userRole === 'hr' || userRole === 'admin';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200,height: '650px', mx: 'auto', borderRadius: 2, border: '2px solid', borderColor: 'grey.300', boxShadow: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <EventIcon sx={{ color: '#1976d2', fontSize: 27 }} />
          <Typography variant="h6"  sx={{fontWeight:"bold" }} >
            Organizational Events
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton
            color="primary"
            onClick={refreshData}
            sx={{ backgroundColor: '#e3f2fd' }}
            disabled={loading}
          >
            <RefreshIcon />
          </IconButton>
          {canCreateEvents && (
            <IconButton
              color="primary"
              onClick={() => setOpenEventDialog(true)}
              sx={{ backgroundColor: '#f3e5f5' }}
            >
              <AddIcon />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Events List - Scrollable container */}
      <Box sx={{ 
        maxHeight: '550px', 
        overflowY: 'auto',
        pr: 1,
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-thumb': { 
          backgroundColor: 'primary.main', 
          borderRadius: '3px' 
        }
      }}>
        <Stack spacing={2}>
          {events.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No upcoming events found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {canCreateEvents ? 'Create your first event to get started!' : 'Check back later for upcoming events.'}
              </Typography>
            </Paper>
          ) : (
            events.map((event) => (
              <Card 
                key={event.id || Math.random()} 
                elevation={0}
                sx={{ 
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
                    borderColor: '#cbd5e1'
                  }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    {/* Left Section - Main Event Info */}
                    <Box sx={{ flexGrow: 1, pr: 1 }}>
                      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={1.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                            {getEventTypeIcon(event.type)}
                          </Box>
                          <Typography variant="subtitle1" component="h2" sx={{ 
                            fontWeight: 700,
                            color: '#1f2937'
                          }}>
                            {event.title || 'Untitled Event'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.75, ml: 'auto' }}>
                          <Chip 
                            label={event.type || 'Event'} 
                            color={getEventTypeColor(event.type)}
                            size="small"
                          />
                          {/* Status Chip */}
                          <Chip 
                            label={event.status || 'pending'} 
                            color={getStatusColor(event.status)}
                            size="small"
                          />
                        </Box>
                      </Box>
                      
                      <Box display="flex" flexWrap="wrap" gap={2} sx={{ mt: 1 }}>
                        <Box display="flex" alignItems="center">
                          <CalendarIcon sx={{ fontSize: 16, mr: 0.75, color: 'primary.main' }} />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {formatDate(event.date)}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" alignItems="center">
                          <TimeIcon sx={{ fontSize: 16, mr: 0.75, color: 'primary.main' }} />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {formatTime(event.time)}
                          </Typography>
                        </Box>
                        
                        {event.location && (
                          <Box display="flex" alignItems="center">
                            <LocationIcon sx={{ fontSize: 16, mr: 0.75, color: 'primary.main' }} />
                            <Typography variant="body2" color="text.secondary">
                              {event.location}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Only show employee count for managers/HR/admin */}
                        {canCreateEvents && (
                          <Box display="flex" alignItems="center">
                            <PeopleIcon sx={{ fontSize: 16, mr: 0.75, color: 'primary.main' }} />
                            <Typography variant="body2" color="text.secondary">
                              {event.assignedCount === 'all' || event.assignedCount === 'All' 
                                ? 'All Employees' 
                                : `${event.assignedCount || 0} Employee(s)`}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Updated attendees display */}
                        <Box display="flex" alignItems="center">
                          <CategoryIcon sx={{ fontSize: 16, mr: 0.75, color: 'primary.main' }} />
                          <Typography variant="body2" color="text.secondary">
                            {event.attendees && event.attendees > 0 
                              ? `Expected: ${event.attendees}`
                              : 'No attendees expected'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {event.creator && (event.creator.firstName || event.creator.lastName) && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Created by: {event.creator.firstName || ''} {event.creator.lastName || ''}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    {/* Right Section - Actions */}
                    {canCreateEvents && (
                      <Box>
                        <IconButton
                          onClick={(e) => handleMenuClick(e, event)}
                          disabled={updatingEvent}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>
      </Box>

      {/* Enhanced Event Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
      {/* <MenuItem onClick={() => startEditEvent(selectedEventForMenu)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Event</ListItemText>
        </MenuItem> */}
        <MenuItem onClick={() => markEventAsCompleted(selectedEventForMenu?.id)}>
          <ListItemIcon>
            <CheckCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark as Completed</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => updateEventStatus(selectedEventForMenu?.id, 'cancelled')}>
          <ListItemIcon>
            <CancelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel Event</ListItemText>
        </MenuItem>
      </Menu>

      {/* Event Dialog */}
      <Dialog 
        open={openEventDialog} 
        onClose={resetForm}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EventIcon color="primary" />
            {editingEvent ? 'Edit Event' : 'Create New Event'}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {/* Event Title */}
            <TextField
              label="Event Title"
              fullWidth
              value={newEvent.title}
              onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
              error={!!validationErrors.title}
              helperText={validationErrors.title}
              variant="outlined"
            />

            {/* Date and Time Row */}
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date"
                  type="date"
                  fullWidth
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                  error={!!validationErrors.date}
                  helperText={validationErrors.date}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Time"
                  type="time"
                  fullWidth
                  value={newEvent.time}
                  onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                  error={!!validationErrors.time}
                  helperText={validationErrors.time}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Location */}
            <TextField
              label="Location (Optional)"
              fullWidth
              value={newEvent.location}
              onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
              variant="outlined"
            />

            {/* Event Type */}
            <FormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={newEvent.type}
                label="Event Type"
                onChange={(e) => {
                  const selectedType = e.target.value;
                  setNewEvent({...newEvent, type: selectedType});
                  setCustomEventType(selectedType === 'Custom');
                }}
              >
                {eventTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getEventTypeIcon(type)}
                      {type}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Custom Event Type */}
            {customEventType && (
              <TextField
                label="Custom Event Type"
                fullWidth
                value={newEvent.customType}
                onChange={(e) => setNewEvent({...newEvent, customType: e.target.value})}
                error={!!validationErrors.customType}
                helperText={validationErrors.customType}
                variant="outlined"
              />
            )}

            {/* Expected Attendees */}
            <TextField
              label="Expected Attendees (Optional)"
              type="number"
              fullWidth
              value={newEvent.attendees}
              onChange={(e) => setNewEvent({...newEvent, attendees: e.target.value})}
              variant="outlined"
              inputProps={{ min: 0 }}
            />

            {/* Employee Assignment */}
            {canCreateEvents && employees.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Assign Employees
                </Typography>
                
                {/* Select All Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectAllEmployees}
                      onChange={(e) => handleSelectAllEmployees(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Assign to All Employees"
                />

                {/* Individual Employee Selection */}
                {!selectAllEmployees && (
                  <Box sx={{ mt: 2, maxHeight: 200, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <List dense>
                      {employees.map((employee) => {
                        const isSelected = newEvent.assignedEmployees.some(emp => emp.id === employee.id);
                        return (
                          <ListItem key={employee.id} disablePadding>
                            <ListItemButton
                              onClick={() => handleEmployeeSelection(employee, !isSelected)}
                              dense
                            >
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={isSelected}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={employee.name}
                                secondary={`${employee.department || 'No Department'} - ${employee.position || 'No Position'}`}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                )}

                {/* Assignment Error */}
                {validationErrors.assignedEmployees && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {validationErrors.assignedEmployees}
                  </Typography>
                )}

                {/* Selected Employees Summary */}
                {!selectAllEmployees && newEvent.assignedEmployees.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Selected Employees ({newEvent.assignedEmployees.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {newEvent.assignedEmployees.map((emp) => (
                        <Chip
                          key={emp.id}
                          label={emp.name}
                          size="small"
                          onDelete={() => handleEmployeeSelection(emp, false)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Status (for editing) */}
            {editingEvent && (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newEvent.status}
                  label="Status"
                  onChange={(e) => setNewEvent({...newEvent, status: e.target.value})}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={resetForm} disabled={addingEvent}>
            Cancel
          </Button>
          <Button
            onClick={editingEvent ? updateEvent : addNewEvent}
            variant="contained"
            disabled={addingEvent}
            startIcon={addingEvent ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {addingEvent ? 'Processing...' : (editingEvent ? 'Update Event' : 'Create Event')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}